import ContentStack, { LivePreview, Region } from 'contentstack';

import { logger } from '@/services/logger';
import { ContentStack as ForgeContentStack } from '@nxt-costco-com/forge-digital-components';
import { ConfigStack as ForgeConfigStack } from '@nxt-costco-com/forge-digital-components';

// Only configure undici on the server side to avoid bundling issues
if (typeof window === 'undefined') {
    (async function configureGlobalFetch() {
        const { Agent, setGlobalDispatcher } = await import('undici');
        const { Dispatcher: _Dispatcher } = await import('undici');
        type DispatchOptions = Parameters<
            InstanceType<typeof _Dispatcher>['dispatch']
        >[0];
        type DispatchHandlers = Parameters<
            InstanceType<typeof _Dispatcher>['dispatch']
        >[1];
        const { httpOutboundDuration, httpOutboundCounter, httpErrorCounter } =
            await import('../../metrics');
        class LoggingDispatcher extends Agent {
            dispatch(options: unknown, handler: unknown) {
                const startTime = Date.now();
                const targetHost = (options as { origin: string }).origin;
                const url = `${(options as { origin: string; path: string }).origin}${(options as { origin: string; path: string }).path}`;
                const method = (options as { method?: string }).method || 'GET';

                const onError = (
                    handler as { onError: (...args: unknown[]) => void }
                ).onError.bind(handler);
                (handler as { onError: (...args: unknown[]) => void }).onError =
                    (...args: unknown[]) => {
                        const duration = Date.now() - startTime;

                        // Sanitize error logging: only log error name and message if available
                        let errorInfo: Record<string, string> = {};
                        const status = 500;
                        if (args && args.length > 0) {
                            const err = args[0];
                            if (err instanceof Error) {
                                errorInfo = {
                                    errorName: err.name,
                                    errorMessage: err.message,
                                };
                            } else if (typeof err === 'string') {
                                errorInfo = { errorMessage: err };
                            } else {
                                errorInfo = { errorType: typeof err };
                            }
                        }

                        httpErrorCounter?.inc({
                            method,
                            //path: url,
                            statusCode: status,
                            errorType: 'server_error',
                        });

                        logger.info({
                            message: 'Request failed',
                            method,
                            url: url,
                            duration: `${duration}ms`,
                            errorInfo,
                        });

                        onError(...args);
                    };

                const originalOnComplete = (
                    handler as { onComplete?: (...args: unknown[]) => void }
                ).onComplete?.bind(handler);

                (
                    handler as {
                        onComplete?: (...args: unknown[]) => void;
                        statusCode?: number;
                    }
                ).onComplete = function (...args: unknown[]) {
                    const endTime = Date.now();
                    const ms = Number(endTime - startTime);
                    const seconds = ms / 1000;
                    const statusCode = ((handler as { statusCode?: number })
                        ?.statusCode || 200) as number;
                    const statusGroup = `${Math.floor(statusCode / 100)}xx`;

                    if (seconds > 2) {
                        logger.info({
                            message: 'Slow request detected',
                            method,
                            url: url,
                            duration: `${ms}ms`,
                            statusCode,
                            statusGroup,
                        });
                    }

                    // Note: URL is not included in metrics to avoid high cardinality.
                    // URL details are logged above for slow requests (>2s) and available in GCP Log Explorer.
                    httpOutboundCounter?.inc({
                        method,
                        target_host: targetHost,
                        statusCode: String(statusCode),
                        statusGroup,
                    });

                    httpOutboundDuration?.observe(
                        {
                            method,
                            target_host: targetHost,
                            statusCode: String(statusCode),
                            statusGroup,
                        },
                        seconds,
                    );
                    try {
                        if (originalOnComplete) originalOnComplete(...args);
                    } catch {
                        // ignore
                    }
                };

                // Call the original dispatcher's dispatch method with wrapped handler
                return super.dispatch(
                    options as DispatchOptions,
                    handler as DispatchHandlers,
                );
            }
        }

        // Create an Agent instance with optimized configurations for connection pooling
        // Settings suggested by copilot based on description of our usecase, but feel free to adjust as needed
        const globalDispatcher = new LoggingDispatcher({
            // Connection pool settings to prevent port exhaustion
            connections: 356, // ✅ Matches your HttpsAgent
            maxCachedSessions: 100, // ✅ Good for TLS reuse

            // Timeout settings - aligned with Envoy
            connectTimeout: 25000, // 🔄 Less than Envoy's 30s
            headersTimeout: 25000, // 🔄 Consistent with connect
            bodyTimeout: 60000, // ✅ Keep for large responses

            // Keep-alive settings - aligned with Envoy
            keepAliveTimeout: 90000, // 🔄 Match Envoy's idleTimeout: 90s
            keepAliveMaxTimeout: 300000, // 🔄 5 minutes (recycle connections in app, not envoy)
            keepAliveTimeoutThreshold: 1000, // ✅ Fine as-is

            // Conservative pipelining
            pipelining: 1, // ✅ Good for external APIs
        });

        setGlobalDispatcher(globalDispatcher);
    })();
}

const debug = process.env.DEBUG_CONTENT_STACK_SDK === 'true' ? true : false;

export const BROWSESTACK = ContentStack.Stack({
    api_key: process.env.CONTENTSTACK_BROWSE_API_KEY as string,
    delivery_token: process.env.CONTENTSTACK_BROWSE_DELIVERY_TOKEN as string,
    environment: process.env.CONTENTSTACK_BROWSE_ENVIRONMENT as string,
    region: process.env.CONTENTSTACK_BROWSE_REGION as ContentStack.Region,
    branch: process.env.CONTENTSTACK_BROWSE_BRANCH as string,
    fetchOptions: {
        debug,
        logHandler(level: string, data: unknown) {
            if (level === 'warning' || level === 'error') {
                logger.error(data, `level: ${level}`);
            }
        },
    },
});

export const BROWSESTACK_LIVEPREVIEW = ContentStack.Stack({
    api_key: process.env.NEXT_PUBLIC_CONTENTSTACK_BROWSE_API_KEY as string,
    delivery_token: process.env
        .NEXT_PUBLIC_CONTENTSTACK_BROWSE_DELIVERY_TOKEN as string,
    environment: process.env
        .NEXT_PUBLIC_CONTENTSTACK_BROWSE_ENVIRONMENT as string,
    region: process.env
        .NEXT_PUBLIC_CONTENTSTACK_BROWSE_REGION as ContentStack.Region,
    branch: process.env.NEXT_PUBLIC_CONTENTSTACK_BROWSE_BRANCH as string,
    live_preview: {
        enable: true,
        preview_token: process.env
            .NEXT_PUBLIC_CONTENTSTACK_BROWSE_PREVIEW_TOKEN as string,
        host: process.env
            .NEXT_PUBLIC_CONTENTSTACK_BROWSE_PREVIEW_HOST as string,
    } as LivePreview,
});

export const CONFIGSTACK = ContentStack.Stack({
    api_key: process.env.CONTENTSTACK_CONFIG_API_KEY as string,
    delivery_token: process.env.CONTENTSTACK_CONFIG_DELIVERY_TOKEN as string,
    environment: process.env.CONTENTSTACK_CONFIG_ENVIRONMENT as string,
    region: process.env.CONTENTSTACK_CONFIG_REGION as ContentStack.Region,
    branch: process.env.CONTENTSTACK_CONFIG_BRANCH as string,
    fetchOptions: {
        debug,
        logHandler(level: string, data: unknown) {
            if (level === 'warning' || level === 'error') {
                logger.error(data, `level: ${level}`);
            }
        },
    },
});

export const CONFIGSTACK_LIVEPREVIEW = ContentStack.Stack({
    api_key: process.env.NEXT_PUBLIC_CONTENTSTACK_CONFIG_API_KEY as string,
    delivery_token: process.env
        .NEXT_PUBLIC_CONTENTSTACK_CONFIG_DELIVERY_TOKEN as string,
    environment: process.env
        .NEXT_PUBLIC_CONTENTSTACK_CONFIG_ENVIRONMENT as string,
    region: process.env
        .NEXT_PUBLIC_CONTENTSTACK_CONFIG_REGION as ContentStack.Region,
    branch: process.env.NEXT_PUBLIC_CONTENTSTACK_CONFIG_BRANCH as string,
});

export const configureStacks = (settings?: { fetchOptions: unknown }) => {
    ForgeContentStack.configure({
        api_key: process.env.CONTENTSTACK_BROWSE_API_KEY as string,
        branch: process.env.CONTENTSTACK_BROWSE_BRANCH as string,
        delivery_token: process.env
            .CONTENTSTACK_BROWSE_DELIVERY_TOKEN as string,
        environment: process.env.CONTENTSTACK_BROWSE_ENVIRONMENT as string,
        fetchOptions: {
            debug,
            logHandler(level: string, data: unknown) {
                if (level === 'warning' || level === 'error') {
                    logger.error(data, `level: ${level}`);
                }
            },
            retryLimit: 3,
            ...(settings?.fetchOptions || {}),
        },
        ...(process.env.CONTENTSTACK_BROWSE_REGION && {
            region: process.env.CONTENTSTACK_BROWSE_REGION as Region,
        }),
    });

    ForgeConfigStack.configure(
        {
            api_key: process.env.CONTENTSTACK_CONFIG_API_KEY as string,
            branch: process.env.CONTENTSTACK_CONFIG_BRANCH as string,
            delivery_token: process.env
                .CONTENTSTACK_CONFIG_DELIVERY_TOKEN as string,
            environment: process.env.CONTENTSTACK_CONFIG_ENVIRONMENT as string,
            fetchOptions: {
                debug,
                logHandler(level: string, data: unknown) {
                    if (level === 'warning' || level === 'error') {
                        logger.error(data, `level: ${level}`);
                    }
                },
                retryLimit: 3,
                ...(settings?.fetchOptions || {}),
            },
            ...(process.env.CONTENTSTACK_CONFIG_REGION && {
                region: process.env.CONTENTSTACK_CONFIG_REGION as Region,
            }),
        },
        {
            CONTENT_ENTRY_ENVIRONMENT_CLIENT_CONFIGURATION:
                process.env.CONTENT_ENTRY_ENVIRONMENT_CLIENT_CONFIGURATION,
            CONTENT_ENTRY_ID_CLIENT_CONFIGURATION:
                process.env.CONTENT_ENTRY_ID_CLIENT_CONFIGURATION,
        },
    );
};

export const EXCEPTIONS = [
    'ACL',
    '_in_progress',
    'created_at',
    'created_by',
    '_metadata',
    'tags',
    'updated_by',
    'publish_details',
];
